import { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { REGISTRATION_ENABLED } from '@/lib/utils';
import { useSplitPaymentStatus } from '@/hooks/useSplitPaymentStatus';
import SplitPaymentStatusCard from '../SplitPaymentStatusCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Calendar, MapPin, Users, Trophy, Clock, User, Award, Info, KeyRound } from 'lucide-react';
import PrizesDialog, { CategoryPrizes } from '../PrizesDialog';
import { usePrizes } from '@/hooks/usePrizes';
import { useIsMobile } from '@/hooks/use-mobile';
import { useEventRules } from '@/hooks/useEventRules';
import FormatInfoDialog from '../FormatInfoDialog';
import { useLanguage } from '@/contexts/LanguageContext';

const conductRuleTranslations: Record<string, string> = {
  "Respeto hacia árbitros y rivales": "Respect towards referees and opponents",
  "Vestimenta deportiva apropiada": "Appropriate sports attire",
};

interface Tournament {
  id: number | string;
  name: string;
  date: string;
  endDate: string;
  modality: 'singles' | 'doubles';
  gameType: 'singles' | 'doubles';
  sport: 'tennis' | 'padel' | 'pickleball';
  category: string;
  subcategory: string;
  partner: string;
  currentStage: string;
  status: string;
  location?: string; // Add location field
}
interface TournamentInfoProps {
  tournament: Tournament;
  prizes?: CategoryPrizes[];
  timeRange?: string; // New prop for time range
  tournamentFormat?: string; // New prop for tournament format
  onContactOrganizer?: () => void; // New prop for contact organizer functionality
  currentView?: 'groups' | 'elimination'; // New prop to indicate current view context
  userRegistration?: {
    category: string | null;
    subcategory: string | null;
  } | null; // User's registration info for filtering prizes
  userRegistrations?: Array<{ id?: string; category: string | null; subcategory: string | null; partner_name?: string | null; partner_access_code?: string | null }>;
  onRegisterClick?: () => void;
  onAccessCodeClick?: () => void;
  isUserRegistered?: boolean;
  registrationLoading?: boolean;
  partnerAccessCode?: string | null;
  groupRules?: {
    setsPerMatch?: number;
    goldenPoint?: boolean;
    superTieBreak?: boolean;
    maxGamesPerSet?: number;
  };
}

const TournamentInfo = ({
  tournament,
  prizes = [],
  timeRange = "09:00 - 11:00",
  tournamentFormat,
  onContactOrganizer,
  currentView = 'groups',
  userRegistration,
  userRegistrations,
  onRegisterClick,
  onAccessCodeClick,
  isUserRegistered = false,
  registrationLoading = false,
  partnerAccessCode,
  groupRules
}: TournamentInfoProps) => {
  const isMobile = useIsMobile();
  const { t, language } = useLanguage();
  const [showFormatDialog, setShowFormatDialog] = useState(false);
  const navigate = useNavigate();
  const { splitPayment, clear: clearSplitPayment } = useSplitPaymentStatus(tournament.id.toString());
  const isPickleball = (tournament.sport as string)?.toLowerCase() === 'pickleball';

  const handlePayPartner = (sp: import('@/hooks/useSplitPaymentStatus').SplitPaymentData) => {
    navigate(`/split-payment/${sp.id}`);
  };

  // Get real prizes from database
  const {
    prizes: dbPrizes
  } = usePrizes(tournament.id.toString());
  
  // Get real rules from database
  const { rules: eventRules, loading: rulesLoading } = useEventRules(tournament.id.toString());
  
  const translateCategory = (cat: string): string => {
    if (language === 'es') return cat;
    const normalized = cat.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    const prefixes: [string, string][] = [
      ['masculin', 'categories.masculino'],
      ['femenin', 'categories.femenino'],
      ['mix', 'categories.mixto'],
    ];
    for (const [prefix, key] of prefixes) {
      if (normalized.startsWith(prefix)) return t(key);
    }
    return cat;
  };

  const translateSubcategory = (sub: string): string => {
    if (language === 'es') return sub;
    const key = `subcategories.${sub}`;
    const translated = t(key);
    return translated !== key ? translated : sub;
  };

  console.log('TournamentInfo - tournamentFormat received:', tournamentFormat);

  // Calculate days until tournament starts
  const getDaysUntilStart = (): number => {
    const startDate = new Date(tournament.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    startDate.setHours(0, 0, 0, 0);
    const diffTime = startDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  const getSportColor = (sport: string) => {
    switch (sport.toLowerCase()) {
      case 'tennis':
        return 'bg-green-100 text-green-800';
      case 'padel':
        return 'bg-emerald-100 text-emerald-800';
      case 'pickleball':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getSportIcon = (sport: string) => {
    switch (sport.toLowerCase()) {
      case 'tennis':
        return <span className="text-base">🎾</span>;
      case 'padel':
        return <img src="/lovable-uploads/5b084fa7-1691-4ba9-84d3-9c2aaabd012a.png" alt="Padel" className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />;
      case 'pickleball':
        return <img src="/lovable-uploads/1bcd1940-66b0-4c8c-aa95-3f6e609dd00b.png" alt="Pickleball" className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />;
      default:
        return <span className="text-base">🏆</span>;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const getFormatDisplayName = () => {
    if (!tournamentFormat) return t('tournament.notSpecified');
    
    switch (tournamentFormat.toLowerCase()) {
      case 'round-robin':
      case 'round_robin':
        return 'Round Robin';
      case 'eliminacion-directa':
      case 'eliminacion_directa':
        return t('tournament.eliminationDirect');
      case 'eliminacion-doble':
      case 'eliminacion_doble':
        return t('tournament.doubleElimination');
      case 'sumatoria-juegos':
      case 'sumatoria_juegos':
        return t('tournament.sumOfGames');
      default:
        return tournamentFormat;
    }
  };
  
  const getStatusBadge = () => {
    if (tournament.status === 'upcoming') {
      return <Badge variant="outline" className="bg-yellow-100 text-yellow-700">{t('tournament.statusUpcoming')}</Badge>;
    }
    return <Badge variant="outline" className="bg-green-100 text-green-700">{t('tournament.activeTournament')}</Badge>;
  };
  const getFormatBadge = () => {
    if (!tournamentFormat) return null;
    switch (tournamentFormat) {
      case 'round-robin':
        return <Badge className="bg-purple-100 text-purple-700">Round Robin</Badge>;
      case 'eliminacion-directa':
        return <Badge className="bg-amber-100 text-amber-700">{t('tournament.eliminationDirect')}</Badge>;
      case 'sumatoria-juegos':
        return <Badge className="bg-indigo-100 text-indigo-700">{t('tournament.sumOfGames')}</Badge>;
      default:
        return <Badge className="bg-purple-100 text-purple-700">Torneo</Badge>;
    }
  };

  // Generate prizes using same logic as EventCard
  const generateAllCategoryPrizes = (): CategoryPrizes[] => {
    // First, try to use real prizes from database
    if (dbPrizes && dbPrizes.length > 0) {
      // Convert database prizes to CategoryPrizes format
      const categoryPrizesMap: Record<string, CategoryPrizes> = {};
      dbPrizes.forEach(prize => {
        const key = prize.category;
        if (!categoryPrizesMap[key]) {
          // Parse category format: "Masculino 1era" or "Femenino 1era"
          const parts = prize.category.includes(' - ') ? prize.category.split(' - ') : [prize.category];
          let genderCategory = parts[0];
          let subcategory = parts[1] || '';

          // Handle formats like "Masculino 1era" (no dash separator)
          if (!subcategory && genderCategory.includes(' ')) {
            const spaceParts = genderCategory.split(' ');
            if (spaceParts.length >= 2) {
              genderCategory = spaceParts[0];
              subcategory = spaceParts.slice(1).join(' ');
            }
          }
          categoryPrizesMap[key] = {
            category: genderCategory,
            subcategory: subcategory || '1era',
            firstPlace: [],
            secondPlace: []
          };
        }

        // Parse prize description to determine type and place
        const description = prize.description.toLowerCase();
        const originalDescription = prize.description;
        const isSecondPlace = description.includes('segundo') || description.includes('2do') || description.includes('2°');
        let prizeObj;

        // Check for monetary values (like "$2000")
        if (originalDescription.includes('$') || originalDescription.includes('€') || description.includes('dinero') || prize.amount) {
          // Extract amount from description if it starts with $ or €
          let amount = prize.amount || 0;
          if (originalDescription.includes('$')) {
            const match = originalDescription.match(/\$(\d+)/);
            if (match) {
              amount = parseInt(match[1]);
            }
          } else if (originalDescription.includes('€')) {
            const match = originalDescription.match(/€(\d+)/);
            if (match) {
              amount = parseInt(match[1]);
            }
          }
          prizeObj = {
            type: 'dinero' as const,
            amount,
            description: originalDescription,
          };
        } else if (description.includes('trofeo')) {
          prizeObj = {
            type: 'trofeo' as const,
            description: originalDescription,
          };
        } else if (description.includes('certificado')) {
          prizeObj = {
            type: 'certificado' as const,
            description: originalDescription,
          };
        } else if (description.includes('pala')) {
          prizeObj = {
            type: 'pala' as const,
            description: originalDescription,
          };
        } else if (description.includes('raqueta')) {
          prizeObj = {
            type: 'raqueta' as const,
            description: originalDescription,
          };
        } else {
          prizeObj = {
            type: 'otro' as const,
            description: originalDescription
          };
        }

        // Add prize to appropriate place
        if (isSecondPlace) {
          categoryPrizesMap[key].secondPlace?.push(prizeObj);
        } else {
          categoryPrizesMap[key].firstPlace.push(prizeObj);
        }
      });
      return Object.values(categoryPrizesMap);
    }

    // Fallback to provided prizes (from props) only — no hardcoded defaults
    if (prizes && prizes.length > 0) {
      return prizes;
    }

    // Return empty array so the "no prizes" empty state shows
    return [];
  };

  // Función para obtener las reglas según la vista actual con datos reales de la BD
  const getRulesContent = () => {
    if (rulesLoading) {
      return <p className="text-sm text-muted-foreground">{t('tournament.loadingRules')}</p>;
    }

    if (!eventRules) {
      return <p className="text-sm text-muted-foreground">{t('tournament.noRulesConfigured')}</p>;
    }

    const { groupRules, additionalRules } = eventRules;

    // Para round-robin, las reglas cambian según la vista actual
    if (tournamentFormat === 'round-robin') {
      if (currentView === 'elimination') {
        // Vista de eliminación directa
        return (
          <div className="space-y-4">
            {/* Reglas de Juego - Eliminación */}
            <div className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="flex items-center gap-3 mb-3 bg-gradient-to-r from-primary to-primary/70 px-4 py-2 rounded-md">
                <div className="bg-white/20 p-2 rounded-full">
                  <Trophy className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-semibold text-base text-white">{t('tournament.gameRulesEliminationPhase')}</h4>
              </div>
              <ul className="space-y-2.5 text-sm">
                <li className="flex items-start gap-3">
                  <span className="text-amber-600 mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{t('tournament.formatBestOf')} {groupRules.setsPerMatch} sets</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-amber-600 mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{t('tournament.gamesPerSet')}: {groupRules.maxGamesPerSet}</span>
                </li>
                {!isPickleball && (
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-0.5 text-lg">•</span>
                    <span className="text-foreground">{t('tournament.goldenPoint')}: {groupRules.goldenPoint ? t('tournament.yesApplies') : t('tournament.noApplies')}</span>
                  </li>
                )}
                {!isPickleball && (
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-0.5 text-lg">•</span>
                    <span className="text-foreground">{t('tournament.superTieBreak')}: {groupRules.superTieBreak ? t('tournament.yesApplies') : t('tournament.noApplies')}</span>
                  </li>
                )}
                <li className="flex items-start gap-3">
                  <span className="text-amber-600 mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{t('tournament.eliminationLoserOut')}</span>
                </li>
                {groupRules.rules && groupRules.rules.length > 0 && groupRules.rules.map((rule, index) => (
                  <li key={`custom-${index}`} className="flex items-start gap-3">
                    <span className="text-amber-600 mt-0.5 text-lg">•</span>
                    <span className="text-foreground">{rule}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      } else {
        // Vista de grupos
        return (
          <div className="space-y-4">
            {/* Reglas de Juego - Fase de Grupos */}
            <div className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="flex items-center gap-3 mb-3 bg-gradient-to-r from-primary to-primary/70 px-4 py-2 rounded-md">
                <div className="bg-white/20 p-2 rounded-full">
                  <Trophy className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-semibold text-base text-white">{t('tournament.gameRulesGroupPhase')}</h4>
              </div>
              <ul className="space-y-2.5 text-sm">
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{t('tournament.formatBestOf')} {groupRules.setsPerMatch} sets</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{t('tournament.gamesPerSet')}: {groupRules.maxGamesPerSet}</span>
                </li>
                {!isPickleball && (
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-0.5 text-lg">•</span>
                    <span className="text-foreground">{t('tournament.goldenPoint')}: {groupRules.goldenPoint ? t('tournament.yesApplies') : t('tournament.noApplies')}</span>
                  </li>
                )}
                {!isPickleball && (
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-0.5 text-lg">•</span>
                    <span className="text-foreground">{t('tournament.superTieBreak')}: {groupRules.superTieBreak ? t('tournament.yesApplies') : t('tournament.noApplies')}</span>
                  </li>
                )}
                {groupRules.rules && groupRules.rules.length > 0 && groupRules.rules.map((rule, index) => (
                  <li key={`custom-${index}`} className="flex items-start gap-3">
                    <span className="text-primary mt-0.5 text-lg">•</span>
                    <span className="text-foreground">{rule}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Tiempos */}
            <div className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="flex items-center gap-3 mb-3 bg-gradient-to-r from-primary/60 to-primary px-4 py-2 rounded-md">
                <div className="bg-white/20 p-2 rounded-full">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-semibold text-base text-white">{t('tournament.timingSection')}</h4>
              </div>
              <ul className="space-y-2.5 text-sm">
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{t('tournament.warmup')}: {additionalRules.timing.warmupMinutes} {t('tournament.minutes')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{t('tournament.restBetweenSets')}: {additionalRules.timing.restMinutes} {t('tournament.minuteParens')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{t('tournament.sideChange')}: {additionalRules.timing.sideChangeSeconds} {t('tournament.minutes')}</span>
                </li>
                {additionalRules.timing.rules.map((rule, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-primary mt-0.5 text-lg">•</span>
                    <span className="text-foreground">{rule}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Conducta y Tolerancia */}
            <div className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="flex items-center gap-3 mb-3 bg-gradient-to-r from-emerald-400 to-green-500 px-4 py-2 rounded-md">
                <div className="bg-white/20 p-2 rounded-full">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-semibold text-base text-white">{t('tournament.conductSection')}</h4>
              </div>
              <ul className="space-y-2.5 text-sm">
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{t('tournament.arrivalTolerance')}: {additionalRules.conduct.toleranceMinutes} {t('tournament.minutes')}</span>
                </li>
                {additionalRules.conduct.rules.map((rule, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-primary mt-0.5 text-lg">•</span>
                    <span className="text-foreground">{language === 'en' && conductRuleTranslations[rule] ? conductRuleTranslations[rule] : rule}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      }
    } else if (tournamentFormat === 'eliminacion-directa') {
      return (
        <div className="space-y-4">
          {/* Reglas de Juego - Eliminación Directa */}
          <div className="bg-muted/40 rounded-lg p-4 border border-border">
            <div className="flex items-center gap-3 mb-3 bg-gradient-to-r from-primary to-primary/70 px-4 py-2 rounded-md">
              <div className="bg-white/20 p-2 rounded-full">
                <Trophy className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-semibold text-base text-white">{isPickleball ? t('tournament.gameRulesPickleballTournament') : t('tournament.gameRulesFlashTournament')}</h4>
            </div>
            <ul className="space-y-2.5 text-sm">
              <li className="flex items-start gap-3">
                <span className="text-amber-600 mt-0.5 text-lg">•</span>
                <span className="text-foreground">{t('tournament.formatSingleSet')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 mt-0.5 text-lg">•</span>
                <span className="text-foreground">{t('tournament.gamesPerSet')}: {groupRules.maxGamesPerSet}</span>
              </li>
              {!isPickleball && (
                <li className="flex items-start gap-3">
                  <span className="text-amber-600 mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{t('tournament.goldenPoint')}: {groupRules.goldenPoint ? t('tournament.yesApplies') : t('tournament.noApplies')}</span>
                </li>
              )}
              {!isPickleball && (
                <li className="flex items-start gap-3">
                  <span className="text-amber-600 mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{t('tournament.superTieBreak')}: {groupRules.superTieBreak ? t('tournament.yesApplies') : t('tournament.noApplies')}</span>
                </li>
              )}
              {groupRules.rules && groupRules.rules.length > 0 && groupRules.rules.map((rule, index) => (
                <li key={`custom-${index}`} className="flex items-start gap-3">
                  <span className="text-amber-600 mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{rule}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Tiempos */}
          <div className="bg-muted/40 rounded-lg p-4 border border-border">
            <div className="flex items-center gap-3 mb-3 bg-gradient-to-r from-primary/60 to-primary px-4 py-2 rounded-md">
              <div className="bg-white/20 p-2 rounded-full">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-semibold text-base text-white">{t('tournament.timingSection')}</h4>
            </div>
            <ul className="space-y-2.5 text-sm">
              <li className="flex items-start gap-3">
                <span className="text-amber-600 mt-0.5 text-lg">•</span>
                <span className="text-foreground">{t('tournament.warmup')}: {additionalRules.timing.warmupMinutes} {t('tournament.minutes')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 mt-0.5 text-lg">•</span>
                <span className="text-foreground">{t('tournament.restBetweenSets')}: {additionalRules.timing.restMinutes} {t('tournament.minuteParens')}</span>
              </li>
              {additionalRules.timing.rules.map((rule, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-amber-600 mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{rule}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Conducta */}
          <div className="bg-muted/40 rounded-lg p-4 border border-border">
            <div className="flex items-center gap-3 mb-3 bg-gradient-to-r from-emerald-400 to-green-500 px-4 py-2 rounded-md">
              <div className="bg-white/20 p-2 rounded-full">
                <Users className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-semibold text-base text-white">{t('tournament.conductOnly')}</h4>
            </div>
            <ul className="space-y-2.5 text-sm">
              <li className="flex items-start gap-3">
                <span className="text-amber-600 mt-0.5 text-lg">•</span>
                <span className="text-foreground">{t('tournament.arrivalTolerance')}: {additionalRules.conduct.toleranceMinutes} {t('tournament.minutes')}</span>
              </li>
              {additionalRules.conduct.rules.map((rule, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-amber-600 mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{language === 'en' && conductRuleTranslations[rule] ? conductRuleTranslations[rule] : rule}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    } else if (tournamentFormat === 'sumatoria-juegos') {
      return (
        <div className="space-y-4">
          {/* Reglas de Juego - Sumatoria de Juegos */}
          <div className="bg-muted/40 rounded-lg p-4 border border-border">
            <div className="flex items-center gap-3 mb-3 bg-gradient-to-r from-primary to-primary/70 px-4 py-2 rounded-md">
              <div className="bg-white/20 p-2 rounded-full">
                <Trophy className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-semibold text-base text-white">{t('tournament.gameRulesSumOfGames')}</h4>
            </div>
            <ul className="space-y-2.5 text-sm">
              <li className="flex items-start gap-3">
                <span className="text-purple-600 mt-0.5 text-lg">•</span>
                <span className="text-foreground">{t('tournament.formatBestOf')} {groupRules.setsPerMatch} sets</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-purple-600 mt-0.5 text-lg">•</span>
                <span className="text-foreground">{t('tournament.gamesPerSet')}: {groupRules.maxGamesPerSet}</span>
              </li>
              {!isPickleball && (
                <li className="flex items-start gap-3">
                  <span className="text-purple-600 mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{t('tournament.goldenPoint')}: {groupRules.goldenPoint ? t('tournament.yesApplies') : t('tournament.noApplies')}</span>
                </li>
              )}
              <li className="flex items-start gap-3">
                <span className="text-purple-600 mt-0.5 text-lg">•</span>
                <span className="text-foreground">{t('tournament.classificationByTotalGames')}</span>
              </li>
            </ul>
          </div>

          {/* Tiempos */}
          <div className="bg-muted/40 rounded-lg p-4 border border-border">
            <div className="flex items-center gap-3 mb-3 bg-gradient-to-r from-sky-400 to-blue-500 px-4 py-2 rounded-md">
              <div className="bg-white/20 p-2 rounded-full">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-semibold text-base text-white">{t('tournament.timingSection')}</h4>
            </div>
            <ul className="space-y-2.5 text-sm">
              <li className="flex items-start gap-3">
                <span className="text-purple-600 mt-0.5 text-lg">•</span>
                <span className="text-foreground">{t('tournament.warmup')}: {additionalRules.timing.warmupMinutes} {t('tournament.minutes')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-purple-600 mt-0.5 text-lg">•</span>
                <span className="text-foreground">{t('tournament.restBetweenSets')}: {additionalRules.timing.restMinutes} {t('tournament.minuteParens')}</span>
              </li>
              {additionalRules.timing.rules.map((rule, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-purple-600 mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{rule}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Conducta */}
          <div className="bg-muted/40 rounded-lg p-4 border border-border">
            <div className="flex items-center gap-3 mb-3 bg-gradient-to-r from-emerald-400 to-green-500 px-4 py-2 rounded-md">
              <div className="bg-white/20 p-2 rounded-full">
                <Users className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-semibold text-base text-white">{t('tournament.conductOnly')}</h4>
            </div>
            <ul className="space-y-2.5 text-sm">
              <li className="flex items-start gap-3">
                <span className="text-purple-600 mt-0.5 text-lg">•</span>
                <span className="text-foreground">{t('tournament.arrivalTolerance')}: {additionalRules.conduct.toleranceMinutes} {t('tournament.minutes')}</span>
              </li>
              {additionalRules.conduct.rules.map((rule, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-purple-600 mt-0.5 text-lg">•</span>
                  <span className="text-foreground">{language === 'en' && conductRuleTranslations[rule] ? conductRuleTranslations[rule] : rule}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    }
    return null;
  };
  
  return (
    <Card className="bg-white border-0 shadow-lg">
      <CardContent className={isMobile ? 'p-4' : 'p-5'}>
        {/* Action buttons row */}
        <div className={`grid grid-cols-2 ${isMobile ? 'gap-2' : 'gap-3'} mb-4`}>
          <PrizesDialog 
            eventTitle={tournament.name} 
            sport={tournament.sport} 
            prizes={generateAllCategoryPrizes()}
            userCategory={userRegistration && userRegistration.category && userRegistration.subcategory 
              ? `${userRegistration.category} ${userRegistration.subcategory}` 
              : undefined}
          >
            <Button 
              variant="outline" 
              className={`w-full bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 hover:border-yellow-400 hover:from-yellow-100 hover:to-orange-100 text-yellow-800 font-semibold shadow-md hover:shadow-lg transition-all hover:scale-105 ${isMobile ? 'h-auto py-2.5 px-2 flex-col gap-0.5' : 'h-12 gap-2'}`}
            >
              <Trophy className={`${isMobile ? 'h-4 w-4' : 'h-4 w-4'} flex-shrink-0`} />
              <span className={isMobile ? 'text-xs leading-tight' : 'text-sm'}>{t('tournament.prizes')}</span>
            </Button>
          </PrizesDialog>

          {tournamentFormat && (
            <Dialog>
              <DialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className={`w-full bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 hover:border-blue-400 hover:from-blue-100 hover:to-indigo-100 text-blue-800 font-semibold shadow-md hover:shadow-lg transition-all hover:scale-105 ${isMobile ? 'h-auto py-2.5 px-2 flex-col gap-0.5' : 'h-12 gap-2'}`}
                >
                  <Clock className={`${isMobile ? 'h-4 w-4' : 'h-4 w-4'} flex-shrink-0`} />
                  <span className={isMobile ? 'text-xs leading-tight' : 'text-sm'}>{t('tournament.rules')}</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md max-h-[80vh]">
                <DialogHeader>
                  <DialogTitle>{t('tournament.tournamentRules')}</DialogTitle>
                </DialogHeader>
                <ScrollArea className="h-[60vh] pr-4">
                  {getRulesContent()}
                </ScrollArea>
              </DialogContent>
            </Dialog>
          )}

        </div>

        {/* Compact info section */}
        <div className="space-y-3">


          {/* Category and modality row */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs text-muted-foreground">{t('tournament.categoryLabel')}:</span>
              {userRegistrations && userRegistrations.length > 1 ? (
                userRegistrations.map((r, i) => (
                  <Badge key={i} variant="outline" className="bg-[#31633f]/15 text-[#31633f] border-[#31633f]/30 text-xs px-1.5 py-0.5">
                    {translateCategory(r.category || '')}{r.subcategory && !r.category?.endsWith(r.subcategory) ? ` - ${translateSubcategory(r.subcategory)}` : ''}
                  </Badge>
                ))
              ) : (
                <Badge variant="outline" className="bg-[#31633f]/15 text-[#31633f] border-[#31633f]/30 text-xs px-1.5 py-0.5">
                  {translateCategory(tournament.category)}{tournament.subcategory && !tournament.category?.endsWith(tournament.subcategory) ? ` - ${translateSubcategory(tournament.subcategory)}` : ''}
                </Badge>
              )}
            </div>


            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">{t('tournament.stageLabel')}:</span>
              <Badge variant="outline" className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5">
                {tournamentFormat === 'round-robin' ? t('tournament.groupPhase') : tournament.currentStage}
              </Badge>
            </div>
          </div>

          {/* Partner info if doubles */}
          {tournament.modality === 'doubles' && tournament.partner && (
            userRegistrations && userRegistrations.length > 1 ? (
              <div className="flex flex-col gap-1.5 text-sm text-muted-foreground">
                {userRegistrations.map((reg, i) => (
                  <div key={reg.id || i} className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center flex-wrap gap-1">
                      <User className="h-4 w-4 mr-2 text-[#31633f]" />
                      <span>{t('tournament.partnerLabel')}:{' '}
                        <span className="font-medium text-foreground">
                          {reg.partner_name || '-'}
                        </span>
                      </span>
                    </div>
                    {reg.partner_access_code && (
                      <Badge
                        variant="outline"
                        className="cursor-pointer bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100 font-mono text-xs tracking-wider"
                        onClick={() => {
                          navigator.clipboard.writeText(reg.partner_access_code!);
                          toast.success(language === 'en' ? 'Code copied!' : '¡Código copiado!');
                        }}
                        title={language === 'en' ? 'Click to copy code' : 'Clic para copiar código'}
                      >
                        <KeyRound className="h-3 w-3 mr-1" />
                        {reg.partner_access_code}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                <div className="flex items-center flex-wrap gap-1">
                  <User className="h-4 w-4 mr-2 text-[#31633f]" />
                  <span>{t('tournament.partnerLabel')}:{' '}
                    <span className="font-medium text-foreground">
                      {tournament.partner}
                    </span>
                  </span>
                </div>
                {partnerAccessCode && (
                  <Badge
                    variant="outline"
                    className="cursor-pointer bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100 font-mono text-xs tracking-wider"
                    onClick={() => {
                      navigator.clipboard.writeText(partnerAccessCode);
                      toast.success(language === 'en' ? 'Code copied!' : '¡Código copiado!');
                    }}
                    title={language === 'en' ? 'Click to copy code' : 'Clic para copiar código'}
                  >
                    <KeyRound className="h-3 w-3 mr-1" />
                    {partnerAccessCode}
                  </Badge>
                )}
              </div>
            )
          )}
        </div>

        {/* Registration buttons - Only show if user is NOT registered */}
        {!isUserRegistered && !registrationLoading && (
          <div className={`grid grid-cols-2 ${isMobile ? 'gap-2' : 'gap-3'} mt-4`}>
            {(() => {
              const hasEventStarted = tournament.status !== 'upcoming';
              const isDisabled = !REGISTRATION_ENABLED || hasEventStarted;
              return (
                <Button
                  onClick={!isDisabled ? onRegisterClick : undefined}
                  disabled={isDisabled}
                  className={`w-full font-semibold shadow-md ${
                    isDisabled
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white'
                  }`}
                >
                  <User className="h-4 w-4 mr-2" />
                  {hasEventStarted ? t('eventDetail.registrationClosed') || 'Inscripciones cerradas' : t('tournament.signMeUp')}
                </Button>
              );
            })()}

            <Button
              variant="outline"
              onClick={onAccessCodeClick}
              className="w-full border-2 border-gray-300 hover:border-gray-400"
            >
              <KeyRound className="h-4 w-4 mr-2" />
              {t('tournament.accessCode')}
            </Button>
          </div>
        )}

        {/* Split Payment Status Card */}
        {splitPayment && (
          <div className="mt-4">
            <SplitPaymentStatusCard
              splitPayment={splitPayment}
              onCancelled={clearSplitPayment}
              onPayPartner={handlePayPartner}
            />
          </div>
        )}
      </CardContent>
      
      {/* Format Info Dialog */}
      <FormatInfoDialog
        formatType={tournamentFormat as any}
        formatName={getFormatDisplayName()}
        groupRules={groupRules}
        isOpen={showFormatDialog}
        onClose={() => setShowFormatDialog(false)}
      />
    </Card>
  );
};
export default TournamentInfo;